import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Download, HelpCircle, Minus, Search, Send, X } from "lucide-react";
import { Badge } from "@/components/Badge";
import { ErrorState } from "@/components/ErrorState";
import { EmptyState } from "@/components/EmptyState";
import { STAGE_COLORS } from "@/lib/constants";
import { getClienteAgenzia, getPrivacyStatus, resendPrivacyLink } from "@/api/agenzia";
import type { PrivacyStatus } from "@/api/agenzia";
import { ApiError } from "@/api/client";
import type { DettaglioClienteAgenzia } from "@/types";
import { formatDateTime } from "@/lib/dates";
import { OnboardingTour } from "@/onboarding/OnboardingTour";
import { dettaglioClienteSteps, dettaglioClienteVersion } from "@/onboarding/dettaglioClienteSteps";
import { useAuth } from "@/auth/useAuth";

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <span className="text-muted-foreground">{label}:</span>{" "}
      <span className="text-text">{value ?? "—"}</span>
    </div>
  );
}

// Riga stato consenso: icona + testo (mai solo colore).
// state: true = sì, false = no, null = mai risposto, undefined = in caricamento.
function PrivacyStatusRow({
  label, state, yes, no,
}: { label: string; state: boolean | null | undefined; yes: string; no: string }) {
  return (
    <div className="flex items-center gap-2">
      {state === true && <Check size={14} className="text-green-700 shrink-0" />}
      {state === false && <X size={14} className="text-red-600 shrink-0" />}
      {(state === null || state === undefined) && (
        <Minus size={14} className="text-muted-foreground shrink-0" />
      )}
      <span className="text-muted-foreground">{label}:</span>
      <span className="text-text">
        {state === undefined ? "…" : state === null ? "mai risposto" : state ? yes : no}
      </span>
    </div>
  );
}

async function downloadDocumento(contactId: string, label: string): Promise<void> {
  const res = await fetch(`/api/espo/agenzia/clienti/${encodeURIComponent(contactId)}/documento`, {
    credentials: "include",
  });
  if (res.status === 404) throw new Error("Documento non disponibile");
  if (res.status === 403) throw new Error("Documento non abilitato per la tua agenzia");
  if (res.status === 502) throw new Error("Impossibile verificare l'abilitazione, riprova");
  if (!res.ok) throw new Error("Errore nel download del documento");

  const cd = res.headers.get("Content-Disposition");
  const cdMatch = cd?.match(/filename="?([^"]+)"?/);
  const filename = cdMatch?.[1] || `documento_${label}`;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function DettaglioClienteAgenziaPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<DettaglioClienteAgenzia | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  const [replayNonce, setReplayNonce] = useState(0);
  const [sendingPrivacyLink, setSendingPrivacyLink] = useState(false);
  const [privacyLinkError, setPrivacyLinkError] = useState<string | null>(null);
  const [privacyLinkSent, setPrivacyLinkSent] = useState(false);
  const [privacyStatus, setPrivacyStatus] = useState<PrivacyStatus | null>(null);
  const { hasRole } = useAuth();
  const isStaff = hasRole("affiliato") || hasRole("operatore");

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    getPrivacyStatus(id)
      .then((s) => { if (!cancelled) setPrivacyStatus(s); })
      .catch(() => { /* sezione stato opzionale: in errore resta il placeholder */ });
    return () => { cancelled = true; };
  }, [id]);

  async function handleResendPrivacyLink() {
    if (!id) return;
    setSendingPrivacyLink(true);
    setPrivacyLinkError(null);
    setPrivacyLinkSent(false);
    try {
      await resendPrivacyLink(id);
      setPrivacyLinkSent(true);
    } catch (e) {
      setPrivacyLinkError(
        e instanceof ApiError && e.detail ? e.detail : "Impossibile inviare il link privacy.",
      );
    } finally {
      setSendingPrivacyLink(false);
    }
  }

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setNotFound(false);
    setError(null);
    getClienteAgenzia(id)
      .then(setData)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) {
          setNotFound(true);
        } else {
          setError("Impossibile caricare il cliente.");
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Il passo sul documento si mostra da sé solo dove la card esiste:
  // OnboardingTour scarta i passi il cui elemento non è in pagina.

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Caricamento...</div>;
  }
  if (notFound) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate("/agenzia/clienti")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-text"
        >
          <ArrowLeft size={16} /> Torna ai clienti
        </button>
        <EmptyState icon={<Search size={26} className="text-brand" />} title="Cliente non trovato" />
      </div>
    );
  }
  if (error) return <ErrorState message={error} />;
  if (!data) return null;

  const fullName = [data.first_name, data.last_name].filter(Boolean).join(" ") || "—";

  return (
    <div className="space-y-4">
      <OnboardingTour
        steps={dettaglioClienteSteps}
        flagKey="dettaglio_cliente"
        version={dettaglioClienteVersion}
        replayNonce={replayNonce}
      />
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate("/agenzia/clienti")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-text"
        >
          <ArrowLeft size={16} /> Torna ai clienti
        </button>
        {isStaff && (
          <button
            type="button"
            onClick={() => setReplayNonce((n) => n + 1)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-text transition-colors"
            title="Rivedi guida"
          >
            <HelpCircle size={14} />
            <span>Rivedi guida</span>
          </button>
        )}
      </div>

      <h2 className="text-lg font-semibold text-text">{fullName}</h2>

      <div data-tour="cliente-anagrafica" className="bg-card p-4 rounded-lg border border-border">
        <h3 className="font-semibold text-text mb-3">Dati anagrafici</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <Field label="Email" value={data.email} />
          <Field label="Telefono" value={data.phone} />
          <Field label="CF / P.IVA" value={data.codice_fiscale} />
          <Field label="IBAN" value={data.iban} />
          <Field label="Indirizzo" value={data.indirizzo} />
          <Field label="N° pratiche" value={String(data.opportunities_count)} />
        </div>
      </div>

      <div data-tour="cliente-privacy" className="bg-card p-4 rounded-lg border border-border">
        <h3 className="font-semibold text-text mb-3">Privacy e consensi</h3>
        <div className="space-y-1.5 text-sm mb-3">
          <div className="flex items-center gap-2">
            {privacyStatus === null ? (
              <>
                <Minus size={14} className="text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">Informativa:</span>
                <span className="text-text">…</span>
              </>
            ) : privacyStatus.presa_visione ? (
              <>
                <Check size={14} className="text-green-700 shrink-0" />
                <span className="text-muted-foreground">Informativa:</span>
                <span className="text-text">confermata dal cliente</span>
              </>
            ) : privacyStatus.informativa_consegnata ? (
              <>
                <Check size={14} className="text-green-700 shrink-0" />
                <span className="text-muted-foreground">Informativa:</span>
                <span className="text-text">consegnata</span>
              </>
            ) : (
              <>
                <Minus size={14} className="text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">Informativa:</span>
                <span className="text-text">non risulta — reinvia il link</span>
              </>
            )}
          </div>
          <PrivacyStatusRow
            label="Email / WhatsApp"
            state={privacyStatus ? privacyStatus.marketing : undefined}
            yes="consenso dato"
            no="consenso negato"
          />
          <PrivacyStatusRow
            label="Telefono"
            state={privacyStatus ? privacyStatus.marketing_telefono : undefined}
            yes="consenso dato"
            no="consenso negato"
          />
        </div>
        {privacyLinkError && (
          <p className="text-sm text-red-600 mb-2">{privacyLinkError}</p>
        )}
        {privacyLinkSent && !privacyLinkError && (
          <p className="text-sm text-green-700 mb-2">Link privacy inviato al cliente.</p>
        )}
        <button
          type="button"
          onClick={handleResendPrivacyLink}
          disabled={sendingPrivacyLink}
          className="flex items-center gap-2 text-sm text-brand hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send size={14} />
          {sendingPrivacyLink ? "Invio…" : "Reinvia link privacy"}
        </button>
      </div>

      {data.has_documento && (
        <div data-tour="cliente-documento" className="bg-card p-4 rounded-lg border border-border">
          <h3 className="font-semibold text-text mb-3">Documento identità</h3>
          {docError && (
            <p className="text-sm text-red-600 mb-2">{docError}</p>
          )}
          <button
            onClick={() => {
              setDocError(null);
              downloadDocumento(data.id, fullName)
                .catch((e) => setDocError(e instanceof Error ? e.message : "Errore download"));
            }}
            className="flex items-center gap-2 text-sm text-brand hover:underline"
          >
            <Download size={14} /> Scarica documento
          </button>
        </div>
      )}

      <div data-tour="cliente-pratiche" className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold text-text">Pratiche di questo cliente</h3>
        </div>
        {data.pratiche.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">Nessuna pratica.</p>
        ) : (
          <>
            {/* Mobile: card list */}
            <div className="sm:hidden divide-y divide-border">
              {data.pratiche.map((p) => {
                const color = STAGE_COLORS[p.stage] ?? STAGE_COLORS["Bozza"];
                return (
                  <div
                    key={p.id}
                    onClick={() => navigate(`/agenzia/pratiche/${p.id}`)}
                    className="p-3 cursor-pointer hover:bg-surface transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-text truncate">{p.name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{formatDateTime(p.created_at)}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <Badge bg={color.bg} text={color.text}>{p.stage}</Badge>
                        {p.quota_agenzia != null && (
                          <span className="text-xs font-medium text-text">
                            €{p.quota_agenzia.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Desktop: table */}
            <table className="hidden sm:table w-full text-sm">
              <thead className="bg-surface text-muted-foreground">
                <tr>
                  <th className="text-left py-2 px-3 font-medium">Data</th>
                  <th className="text-left py-2 px-3 font-medium">Pratica</th>
                  <th className="text-left py-2 px-3 font-medium">Stato</th>
                  <th className="text-right py-2 px-3 font-medium">Commissione</th>
                </tr>
              </thead>
              <tbody>
                {data.pratiche.map((p) => {
                  const color = STAGE_COLORS[p.stage] ?? STAGE_COLORS["Bozza"];
                  return (
                    <tr
                      key={p.id}
                      onClick={() => navigate(`/agenzia/pratiche/${p.id}`)}
                      className="border-t border-border cursor-pointer hover:bg-surface transition-colors"
                    >
                      <td className="py-2 px-3 text-text">
                        {formatDateTime(p.created_at)}
                      </td>
                      <td className="py-2 px-3 text-text">{p.name}</td>
                      <td className="py-2 px-3">
                        <Badge bg={color.bg} text={color.text}>{p.stage}</Badge>
                      </td>
                      <td className="py-2 px-3 text-right text-text">
                        {p.quota_agenzia != null
                          ? `€${p.quota_agenzia.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
